import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { 
    fetchFishbowlData, 
    configureFishbowlStore, 
    verifyFishbowlApproval,
    resetFishbowlConfigureState 
} from './fishbowlSlice';

const steps = [
    'Connection Settings',
    'Authentication Details',
    'Carrier Setup',
    'Administrative Approval'
];

const FishbowlIntegration = () => {
    const dispatch = useDispatch();

    // Redux Global Selectors
    const fishbowlProgress = useSelector(state => state.fishbowl.progress);
    const fishbowlConfigure = useSelector(state => state.fishbowl.configure);
    const savedConfig = fishbowlProgress?.data;

    // Local Input Form Hooks
    const [endpointUrl, setEndpointUrl] = useState('');
    const [portNumber, setPortNumber] = useState('4131');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [carrierId, setCarrierId] = useState('');

    // Workflow Routing Controls
    const [activeStep, setActiveStep] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');

    // Load active settings profile state immediately on mount
    useEffect(() => {
        dispatch(fetchFishbowlData());
        window.isFishbowlManualVerifyAction = false;
    }, [dispatch]);

    // Handle persistent data mapping from DB payload shifts
    useEffect(() => {
        if (savedConfig) {
            setEndpointUrl(savedConfig.endpoint_url || '');
            setPortNumber(savedConfig.port_number || '4131');
            setUsername(savedConfig.username || '');
            setPassword(savedConfig.password || '');
            setCarrierId(savedConfig.carrier_id || '');
            
            // --- FIXED: ALWAYS FORCE FIRST STEP ON OPENING FOR EDITS ---
            // Instead of checking if step equals 3, we always force activeStep back to 0 (First Step) 
            // when editing an existing account configuration.
            setActiveStep(0);
        }
    }, [savedConfig]);

    // Track Redux Configuration updates and handle seamless forward transitions
    useEffect(() => {
        if (fishbowlConfigure?.message) {
            setErrorMsg(fishbowlConfigure.message);
            
            // --- FIXED TRANSITION LOGIC PATHWAY GUARD ---
            if (!fishbowlConfigure.hasErrors) {
                if (activeStep === 2) {
                    setActiveStep(3); // Shift forward to administrative panel ONLY on a success/401 workflow status
                } else {
                    setActiveStep((prev) => prev + 1); // Normal step-forward progression
                }
            } else {
                // A network exception or 422 means handshake failed completely on a bad URL or credentials.
                // Do NOT let Step 2 advance if a true error occurs!
                if (activeStep === 2 && fishbowlConfigure.status !== 401) {
                    // Keep the user strictly locked onto Step 2 (Carrier Setup) so they see the red error box
                    setActiveStep(2); 
                }
            }
            
            dispatch(resetFishbowlConfigureState());
        }
    }, [fishbowlConfigure, activeStep, dispatch]);

    // --- STEP-BY-STEP DISABLED EVALUATOR ---
    const isCurrentStepInvalid = () => {
        if (activeStep === 0) return endpointUrl.trim() === '' || portNumber.trim() === '';
        if (activeStep === 1) return username.trim() === ''; 
        if (activeStep === 2) return carrierId.trim() === '';
        return false;
    };

    const handleNext = () => {
        setErrorMsg('');

        const payload = {
            current_step: activeStep,
            endpoint_url: endpointUrl,
            port_number: portNumber,
            username: username,
            password: password,
            carrier_id: carrierId
        };

        dispatch(configureFishbowlStore(payload));
    };

    const handleBack = () => {
        setErrorMsg('');
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleVerifyApproval = () => {
        setErrorMsg('');
        window.isFishbowlManualVerifyAction = true;
        dispatch(verifyFishbowlApproval());
    };

    const isComponentWorking = fishbowlProgress?.loading || fishbowlConfigure?.loading;

    return (
        <Box sx={{ width: '100%', p: 3, maxWidth: 900, mx: 'auto' }}>
            <Card variant="outlined" sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <CardHeader 
                    title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Fishbowl Inventory Integration</Typography>}
                    subheader="Follow the multi-step configuration process below to synchronize order management workflows."
                    sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb', py: 2.5, px: 3 }}
                />
                <Divider />
                <CardContent sx={{ p: 4 }}>
                    
                    {/* Progress Stepper Mapping Block */}
                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel
                                    StepIconProps={{
                                        sx: {
                                            '&.Mui-active': { color: '#212b36' },
                                            '&.Mui-completed': { color: '#22c55e' }
                                        }
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: activeStep === index ? 700 : 500, color: activeStep === index ? '#161c24' : '#637381' }}>
                                        {label}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {/* Step Content Conditional Controls */}
                    <Box sx={{ minHeight: '220px', mb: 4 }}>
                        {activeStep === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '8px', mb: 1 }}>
                                        Provide the target hostname environment URL routing and designated access port metrics.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        ENDPOINT URL
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. http://wgroupcanadashippingandlogisticsltd.myfishbowl.com"
                                        value={endpointUrl}
                                        onChange={(e) => setEndpointUrl(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        PORT NUMBER
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="4131"
                                        value={portNumber}
                                        onChange={(e) => setPortNumber(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '8px', mb: 1 }}>
                                        Input the API user authorization credentials matching your system specifications.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        USERNAME
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. shippingchimp-api"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        PASSWORD
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="password"
                                        disabled={isComponentWorking}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#f4f6f8', borderRadius: '8px', border: '1px solid #dfe3e8', display: 'flex', gap: 4 }}>
                                        <Typography variant="caption" sx={{ color: '#637381', fontWeight: 600 }}>STATIC APPLICATION METRICS:</Typography>
                                        <Typography variant="caption" sx={{ color: '#212b36', fontWeight: 700 }}>App Name: <b>ShippingChimp</b></Typography>
                                        <Typography variant="caption" sx={{ color: '#212b36', fontWeight: 700 }}>App ID: <b>SC2026</b></Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 2 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2.5, bgcolor: '#fff9f0', borderRadius: '8px', border: '1px solid #ffecb3', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b78103', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <InfoIcon sx={{ fontSize: 18 }} /> Mandatory Action Required in Fishbowl Client Dashboard
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#5c4308', pl: 3.25, lineHeight: 1.6 }}>
                                            1. Log into your Fishbowl client program platform dashboard.<br />
                                            2. Navigate to <b>Admin &gt; Shipping Carriers</b> settings configuration panel.<br />
                                            3. Create a brand new carrier profile named explicitly as <b>"ShippingChimp"</b>.<br />
                                            4. Extract the newly auto-generated internal numeric Carrier Database ID value and enter it below.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', mb: 0.5, display: 'block' }}>
                                        INTERNAL CARRIER ID VALUE
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled={isComponentWorking}
                                        placeholder="e.g. 20"
                                        value={carrierId}
                                        onChange={(e) => setCarrierId(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 3 && (
                            <Box>
                                {!fishbowlProgress?.isApproved ? (
                                    <Box>
                                        <Alert severity="warning" sx={{ borderRadius: '8px', mb: 3 }}>
                                            <AlertTitle sx={{ fontWeight: 700,color:'green' }}>Awaiting Administrator Permission Authorization</AlertTitle>
                                            {errorMsg || "A new integrated application has been added to Fishbowl. Please contact the Fishbowl administrator to approve this integrated application."}
                                        </Alert>
                                        
                                        <Box sx={{ p: 3, border: '1px dashed #b2b2b2', borderRadius: '8px', bgcolor: '#fafafa' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#212b36' }}>
                                                To Approve This Application:
                                            </Typography>
                                            <Typography variant="body2" component="div" sx={{ color: '#637381', pl: 1, '& p': { my: 0.75 } }}>
                                                <p>1. Open the primary Fishbowl Server management console window frame platform.</p>
                                                <p>2. Go to the <b>Integrated Applications</b> module context tab selection list.</p>
                                                <p>3. Find the profile titled <b>"ShippingChimp"</b> and click on <b>Approve / Grant Access</b>.</p>
                                                <p>4. Once approved inside your dashboard, click the verification button below to activate synchronization streams.</p>
                                            </Typography>

                                            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Button 
                                                    variant="contained" 
                                                    disableElevation
                                                    onClick={handleVerifyApproval}
                                                    disabled={isComponentWorking}
                                                    sx={{ bgcolor: '#212b36', '&:hover': { bgcolor: '#161c24' }, textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    {isComponentWorking ? <CircularProgress size={20} color="inherit" /> : "Verify & Finalize Token Activation"}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                                        <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 64, mb: 2 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#161c24', mb: 1 }}>
                                            Integration Successfully Connected!
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#637381', maxWidth: '500px', mx: 'auto', mb: 3 }}>
                                            Authorized secure access token has been generated and validated dynamically. Automatic order tracking and sync structures are fully functional.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                  {errorMsg && activeStep !== 3 && (
                        <Alert 
                            // Force 'error' (Red) if Redux reports an error OR if the message contains failure keywords.
                            // Otherwise, render 'success' (Green).
                            severity={
                                fishbowlConfigure?.hasErrors || 
                                errorMsg.toLowerCase().includes('failed') || 
                                errorMsg.toLowerCase().includes('error') ||
                                errorMsg.toLowerCase().includes('missing')
                                    ? "error" 
                                    : "success"
                            } 
                            sx={{ mb: 3, borderRadius: '8px' }}
                        >
                            {errorMsg}
                        </Alert>
                    )}
                    <Divider />

                    {/* Stepper Navigation Actions Bottom Panel */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="text"
                            disabled={activeStep === 0 || fishbowlProgress?.isApproved || isComponentWorking}
                            onClick={handleBack}
                            sx={{ color: '#637381', fontWeight: 700, textTransform: 'none' }}
                        >
                            Back
                        </Button>
                        
                        {activeStep < 3 && (
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleNext}
                                disabled={isComponentWorking || isCurrentStepInvalid()}
                                sx={{ 
                                    bgcolor: '#212b36', 
                                    '&:hover': { bgcolor: '#161c24' }, 
                                    textTransform: 'none', 
                                    fontWeight: 700,
                                    px: 4,
                                    borderRadius: '6px'
                                }}
                            >
                                {isComponentWorking ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    activeStep === 2 ? 'Authorize Connection Pipeline' : 'Continue'
                                )}
                            </Button>
                        )}
                    </Box>

                </CardContent>
            </Card>
        </Box>
    );
};

export default FishbowlIntegration;